import PageHero from "@/components/PageHero";
import CTABanner from "@/components/CTABanner";
import AwardsStrip from "@/components/AwardsStrip";

const AboutPage = () => {
  return (
    <main data-testid="page-about">
      <PageHero
        eyebrow="About SeaShells"
        title="A small, family-run care company. On purpose."
        subtitle="We started SeaShells because we wanted the kind of care we'd want for our own parents — and we couldn't find it. So we built it. Locally. Carefully. With our names on the door."
      />

      <section className="section">
        <div className="container-px max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="relative rounded-[28px] overflow-hidden aspect-[4/5] shadow-xl">
              <img
                alt="A small team of carers laughing together"
                className="w-full h-full object-cover"
                src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=900&q=80"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <span className="eyebrow">Our story</span>
            <h2 className="font-display text-4xl md:text-5xl mt-4 text-[var(--ss-teal-800)]">
              Care, the way our own family deserved.
            </h2>
            <p className="mt-5 text-lg text-[var(--ss-ink-soft)]">
              SeaShells was born after a family experience too many of us know:
              the search for care for an ageing parent, and the slow realisation
              that the system was built around the system, not the person.
            </p>
            <p className="mt-3 text-[var(--ss-ink-soft)]">
              So we set out to build something different — a deliberately small,
              local, family-run care company where the founders still know every
              carer and every client by name. Where the same trusted face turns
              up at the door. Where care plans are written around an actual life,
              not a generic template.
            </p>
            <p className="mt-3 text-[var(--ss-ink-soft)]">
              Today we're proud to be CQC Rated <span className="font-semibold text-[var(--ss-teal-800)]">Good</span>, members of
              the UKHCA, and quietly recommended by the families we look after
              across Epping Forest, Harlow and Bishop's Stortford.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section bg-[var(--ss-cream)]">
        <div className="container-px max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <span className="eyebrow">What we believe</span>
            <h2 className="font-display text-4xl md:text-5xl mt-4 text-[var(--ss-teal-800)]">
              Five quiet promises we make to every family.
            </h2>
          </div>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              ["fa-handshake", "We answer the phone", "A real person, every time. Not a queue, not a press-1-for-care."],
              ["fa-user-check", "Same carer, every visit", "Trust grows over a thousand small moments. We protect that."],
              ["fa-scale-balanced", "Honest pricing", "Clear, written quotes. No hidden fees. No upsell."],
              ["fa-comments", "Family kept in the loop", "Daily notes, when something changes, you'll hear it from us first."],
              ["fa-shield-heart", "Carers we'd trust with our own", "Hand-picked. Deeply trained. Paid above the average."],
              ["fa-heart-circle-check", "Care we're proud of", "If we wouldn't book it for our own parents, we don't deliver it."],
            ].map(([icon, title, body], i) => (
              <div key={title} className="card-soft" data-testid={`value-${i + 1}`}>
                <div className="w-12 h-12 rounded-full bg-[var(--ss-teal-700)] text-[var(--ss-cream)] flex items-center justify-center text-xl">
                  <i className={`fa-solid ${icon}`}></i>
                </div>
                <h3 className="font-serif text-2xl text-[var(--ss-teal-800)] mt-5">{title}</h3>
                <p className="mt-3 text-[var(--ss-ink-soft)] text-sm leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Awards */}
      <section className="section bg-[var(--ss-cream)]">
        <div className="container-px max-w-6xl mx-auto">
          <AwardsStrip />
        </div>
      </section>

      {/* Stats */}
      <section className="section">
        <div className="container-px max-w-7xl mx-auto grid md:grid-cols-4 gap-8 text-center">
          {[
            ["98%", "Of families would recommend us"],
            ["CQC", "Rated Good"],
            ["1 hour", "Minimum visit length"],
            ["24/7", "Care line, always answered"],
          ].map(([n, l]) => (
            <div key={l}>
              <div className="font-display text-6xl text-[var(--ss-gold-dark)]">
                {n}
              </div>
              <div className="text-sm uppercase tracking-[0.18em] text-[var(--ss-ink-soft)] mt-2">
                {l}
              </div>
            </div>
          ))}
        </div>
      </section>

      <CTABanner
        eyebrow="Come and meet us"
        heading="A cup of tea is the best place to start."
        body="Book a free, no-pressure home assessment with a senior member of the SeaShells family team."
      />
    </main>
  );
};

export default AboutPage;
